/**
 * Types + trust-boundary parsing for teamform-api's getAggregations query
 * (src/graphql/schema/aggregations.graphql on teamform-api). Real-time,
 * DB-aggregated stats — raw counts only, no derived/composed values. See
 * src/util/teamAggregationMetrics.ts for the client-side derivations.
 */

export type ChartPoint = {
  key?: string | null;
  value: number;
};

export type Chart = {
  points: ChartPoint[];
};

export type ExternalManager = {
  personId: string;
  directReportsCount: number;
};

export type TeamAggregations = {
  memberCount?: number | undefined;
  memberFteSum?: number | undefined;
  vacancyCount?: number | undefined;
  agentCount?: number | undefined;
  managerCount?: number | undefined;
  unassignedCount?: number | undefined;
  directReportsMin?: number | null | undefined;
  directReportsMax?: number | null | undefined;
  directReportsAvg?: number | null | undefined;
  directReportsChart?: Chart | null | undefined;
  managementLayersMin?: number | null | undefined;
  managementLayersMax?: number | null | undefined;
  managementLayersAvg?: number | null | undefined;
  managementLayersChart?: Chart | null | undefined;
  externalManagers?: ExternalManager[] | undefined;
  multiTeamOwnerCount?: number | undefined;
  noDirectOwnerCount?: number | undefined;
  reportingLineManagerCount?: number | undefined;
  reportingLineUnassignedCount?: number | undefined;
  reportingLineDirectReportsMin?: number | null | undefined;
  reportingLineDirectReportsMax?: number | null | undefined;
  reportingLineDirectReportsAvg?: number | null | undefined;
  reportingLineDirectReportsChart?: Chart | null | undefined;
  reportingLineManagementLayersMin?: number | null | undefined;
  reportingLineManagementLayersMax?: number | null | undefined;
  reportingLineManagementLayersAvg?: number | null | undefined;
  reportingLineManagementLayersChart?: Chart | null | undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const optionalFiniteNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const toChart = (value: unknown): Chart | undefined => {
  if (!isRecord(value) || !Array.isArray(value["points"])) return undefined;
  const points: ChartPoint[] = [];
  for (const point of value["points"]) {
    if (!isRecord(point)) continue;
    const pointValue = optionalFiniteNumber(point["value"]);
    if (pointValue === undefined) continue;
    const key = typeof point["key"] === "string" ? point["key"] : null;
    points.push({ key, value: pointValue });
  }
  return { points };
};

/** Validate GraphQL Aggregations.team at the trust boundary; calculators trust TeamAggregations. */
export const toTeamAggregations = (value: unknown): TeamAggregations => {
  if (!isRecord(value)) return {};
  const aggregations: TeamAggregations = {};

  const memberCount = optionalFiniteNumber(value["memberCount"]);
  if (memberCount !== undefined) aggregations.memberCount = memberCount;
  const memberFteSum = optionalFiniteNumber(value["memberFteSum"]);
  if (memberFteSum !== undefined) aggregations.memberFteSum = memberFteSum;
  const vacancyCount = optionalFiniteNumber(value["vacancyCount"]);
  if (vacancyCount !== undefined) aggregations.vacancyCount = vacancyCount;
  const agentCount = optionalFiniteNumber(value["agentCount"]);
  if (agentCount !== undefined) aggregations.agentCount = agentCount;
  const managerCount = optionalFiniteNumber(value["managerCount"]);
  if (managerCount !== undefined) aggregations.managerCount = managerCount;
  const unassignedCount = optionalFiniteNumber(value["unassignedCount"]);
  if (unassignedCount !== undefined) {
    aggregations.unassignedCount = unassignedCount;
  }

  const directReportsMin = optionalFiniteNumber(value["directReportsMin"]);
  if (directReportsMin !== undefined) {
    aggregations.directReportsMin = directReportsMin;
  }
  const directReportsMax = optionalFiniteNumber(value["directReportsMax"]);
  if (directReportsMax !== undefined) {
    aggregations.directReportsMax = directReportsMax;
  }
  const directReportsAvg = optionalFiniteNumber(value["directReportsAvg"]);
  if (directReportsAvg !== undefined) {
    aggregations.directReportsAvg = directReportsAvg;
  }
  const directReportsChart = toChart(value["directReportsChart"]);
  if (directReportsChart) aggregations.directReportsChart = directReportsChart;

  const managementLayersMin = optionalFiniteNumber(
    value["managementLayersMin"]
  );
  if (managementLayersMin !== undefined) {
    aggregations.managementLayersMin = managementLayersMin;
  }
  const managementLayersMax = optionalFiniteNumber(
    value["managementLayersMax"]
  );
  if (managementLayersMax !== undefined) {
    aggregations.managementLayersMax = managementLayersMax;
  }
  const managementLayersAvg = optionalFiniteNumber(
    value["managementLayersAvg"]
  );
  if (managementLayersAvg !== undefined) {
    aggregations.managementLayersAvg = managementLayersAvg;
  }
  const managementLayersChart = toChart(value["managementLayersChart"]);
  if (managementLayersChart) {
    aggregations.managementLayersChart = managementLayersChart;
  }

  const externalManagers = value["externalManagers"];
  if (Array.isArray(externalManagers)) {
    const parsed: ExternalManager[] = [];
    for (const em of externalManagers) {
      if (!isRecord(em) || typeof em["personId"] !== "string") continue;
      const directReportsCount = optionalFiniteNumber(em["directReportsCount"]);
      if (directReportsCount === undefined) continue;
      parsed.push({ personId: em["personId"], directReportsCount });
    }
    aggregations.externalManagers = parsed;
  }
  const multiTeamOwnerCount = optionalFiniteNumber(
    value["multiTeamOwnerCount"]
  );
  if (multiTeamOwnerCount !== undefined) {
    aggregations.multiTeamOwnerCount = multiTeamOwnerCount;
  }
  const noDirectOwnerCount = optionalFiniteNumber(value["noDirectOwnerCount"]);
  if (noDirectOwnerCount !== undefined) {
    aggregations.noDirectOwnerCount = noDirectOwnerCount;
  }

  const reportingLineManagerCount = optionalFiniteNumber(
    value["reportingLineManagerCount"]
  );
  if (reportingLineManagerCount !== undefined) {
    aggregations.reportingLineManagerCount = reportingLineManagerCount;
  }
  const reportingLineUnassignedCount = optionalFiniteNumber(
    value["reportingLineUnassignedCount"]
  );
  if (reportingLineUnassignedCount !== undefined) {
    aggregations.reportingLineUnassignedCount = reportingLineUnassignedCount;
  }
  const reportingLineDirectReportsMin = optionalFiniteNumber(
    value["reportingLineDirectReportsMin"]
  );
  if (reportingLineDirectReportsMin !== undefined) {
    aggregations.reportingLineDirectReportsMin = reportingLineDirectReportsMin;
  }
  const reportingLineDirectReportsMax = optionalFiniteNumber(
    value["reportingLineDirectReportsMax"]
  );
  if (reportingLineDirectReportsMax !== undefined) {
    aggregations.reportingLineDirectReportsMax = reportingLineDirectReportsMax;
  }
  const reportingLineDirectReportsAvg = optionalFiniteNumber(
    value["reportingLineDirectReportsAvg"]
  );
  if (reportingLineDirectReportsAvg !== undefined) {
    aggregations.reportingLineDirectReportsAvg = reportingLineDirectReportsAvg;
  }
  const reportingLineDirectReportsChart = toChart(
    value["reportingLineDirectReportsChart"]
  );
  if (reportingLineDirectReportsChart) {
    aggregations.reportingLineDirectReportsChart =
      reportingLineDirectReportsChart;
  }
  const reportingLineManagementLayersMin = optionalFiniteNumber(
    value["reportingLineManagementLayersMin"]
  );
  if (reportingLineManagementLayersMin !== undefined) {
    aggregations.reportingLineManagementLayersMin =
      reportingLineManagementLayersMin;
  }
  const reportingLineManagementLayersMax = optionalFiniteNumber(
    value["reportingLineManagementLayersMax"]
  );
  if (reportingLineManagementLayersMax !== undefined) {
    aggregations.reportingLineManagementLayersMax =
      reportingLineManagementLayersMax;
  }
  const reportingLineManagementLayersAvg = optionalFiniteNumber(
    value["reportingLineManagementLayersAvg"]
  );
  if (reportingLineManagementLayersAvg !== undefined) {
    aggregations.reportingLineManagementLayersAvg =
      reportingLineManagementLayersAvg;
  }
  const reportingLineManagementLayersChart = toChart(
    value["reportingLineManagementLayersChart"]
  );
  if (reportingLineManagementLayersChart) {
    aggregations.reportingLineManagementLayersChart =
      reportingLineManagementLayersChart;
  }

  return aggregations;
};
